import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useGetAiMappingRunAreaResults } from "./hooks";
import { classifyAiMappingError } from "./aiMappingErrors";
import { AiMappingAreaLike, AiMappingResultsLike } from "./aiMappingTypes";
import { AiMappingNotify } from "./aiMappingControllerTypes";
import { unwrap } from "./aiMappingControllerUtils";

interface UseAiMappingResultsOptions {
  projectMapId: string | null;
  unmountedRef: React.MutableRefObject<boolean>;
  areas: AiMappingAreaLike[];
  notify: AiMappingNotify;
  handleError: (error: unknown, options?: { inline?: boolean }) => boolean;
  refreshRun: () => Promise<void>;
}

export function useAiMappingResults({
  projectMapId,
  unmountedRef,
  areas,
  handleError,
  refreshRun,
}: UseAiMappingResultsOptions) {
  const [results, setResults] = useState<AiMappingResultsLike | null>(null);
  const [allResults, setAllResults] = useState<AiMappingResultsLike[]>([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const allResultsRef = useRef<AiMappingResultsLike[]>([]);
  const [fetchResultsQuery] = useGetAiMappingRunAreaResults();

  const applyResults = useCallback((payload: AiMappingResultsLike) => {
    setResults(payload);
    setAllResults((current) => [
      ...current.filter((result) => result.area.id !== payload.area.id),
      payload,
    ]);
  }, []);

  const resultsAreaKey = useMemo(
    () =>
      areas
        .filter((area) => area.currentVersion > 0)
        .map((area) => `${area.id}:${area.currentVersion}`)
        .sort()
        .join(","),
    [areas]
  );

  useEffect(() => {
    if (!resultsAreaKey) {
      setAllResults([]);
      return;
    }
    let cancelled = false;
    const ids = resultsAreaKey.split(",").map((value) => value.split(":")[0]);
    Promise.all(
      ids.map(async (id) => {
        try {
          return unwrap(
            await fetchResultsQuery({ variables: { id }, fetchPolicy: "network-only" }),
            (data) => data?.getAiMappingRunAreaResults
          ) as AiMappingResultsLike;
        } catch {
          return null;
        }
      })
    ).then((loaded) => {
      if (!cancelled) {
        setAllResults(loaded.filter((result): result is AiMappingResultsLike => !!result));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [resultsAreaKey, fetchResultsQuery]);

  const reset = useCallback(() => {
    setResults(null);
    setAllResults([]);
  }, []);

  const loadAreaResults = useCallback(
    async (areaId: string): Promise<AiMappingResultsLike | null> => {
      try {
        const payload = unwrap(
          await fetchResultsQuery({ variables: { id: areaId }, fetchPolicy: "network-only" }),
          (data) => data?.getAiMappingRunAreaResults
        ) as AiMappingResultsLike;
        if (!unmountedRef.current) applyResults(payload);
        return payload;
      } catch (error) {
        if (unmountedRef.current) return null;
        const info = classifyAiMappingError(error);
        if (info.code === "area_not_found" || info.code === "run_not_found") {
          handleError(error);
          await refreshRun();
        } else {
          handleError(error);
        }
        return null;
      }
    },
    [fetchResultsQuery, applyResults, handleError, refreshRun, unmountedRef]
  );

  return {
    results,
    setResults,
    allResults,
    resultsLoading,
    setResultsLoading,
    applyResults,
    reset,
    loadAreaResults,
    refetchResults: loadAreaResults,
  };
}
